import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import { getRecoil } from "recoil-nexus";
import _ from "underscore";
import moment from "moment";
import { consola } from "consola";

import organizationIdState from "../atoms/organizationIdSelector";

/**
 * Custom hook to handle client progress photos
 * Encapsulates functionality for getting, setting, and deleting progress photos
 * @param {string} clientId
 * @param {{ autoLoadProgress?: boolean }} [options] - when autoLoadProgress is false,
 *   only reference photos + config load on client change (month paging loads the rest)
 */
const useClientPhotos = (clientId) => {
  const [startingPhotos, setStartingPhotos] = React.useState(null);
  const [progressPhotos, setProgressPhotos] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [progressPhotoConfig, setProgressPhotoConfig] = React.useState([]);
  const [startingPhotoGroupId, setStartingPhotoGroupId] = React.useState(null);

  // Add a ref to track the last fetched clientId to prevent duplicate calls
  const lastFetchedClientIdRef = React.useRef(null);
  const isFetchingRef = React.useRef(false);

  /**
   * Process photo documents and organize them into groups
   */
  const getPhotoGroups = (docs, limit = 20) => {
    const groups = {};
    let groupCount = 0;
    // Build the groups
    docs.forEach((doc) => {
      const data = doc.data();
      if (data.groupId) {
        if (groupCount <= limit) {
          if (data.groupId in groups) {
            const group = groups[data.groupId];
            group.photos[data.type] = data.url;
            group.metadata.ids.push(data.id);
          } else {
            groupCount += 1;
            if (groupCount <= limit) {
              const groupData = {};
              groupData.photos = {};
              groupData.photos[data.type] = data.url;
              groupData.metadata = {
                createdOn: data.createdOn.toDate(),
                configuration: data.configuration,
                ids: [data.id],
              };
              groups[data.groupId] = groupData;
            }
          }
        }
      }
    });
    return groups;
  };

  /**
   * Get the starting/reference progress photos for a client
   * We use useCallback to maintain a stable function reference
   */
  const getStartingProgressPhotos = React.useCallback(
    async (uid) => {
      // Skip if no uid or if we're already fetching for this client
      if (!uid) return {};
      if (uid === lastFetchedClientIdRef.current && isFetchingRef.current)
        return {};

      // Set the fetching flag and save the clientId
      isFetchingRef.current = true;
      lastFetchedClientIdRef.current = uid;

      setLoading(true);
      try {
        const organizationId = getRecoil(organizationIdState);

        const clientRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(uid);

        const clientData = await clientRef.get();
        if (clientData.exists) {
          const clientDataValues = clientData.data();
          if ("startingPhotoGroupId" in clientDataValues) {
            const photoGroupId = clientDataValues.startingPhotoGroupId;

            // Check if we already have this group ID in state to avoid redundant fetches
            if (photoGroupId === startingPhotoGroupId) {
              isFetchingRef.current = false;
              return startingPhotos || {};
            }

            // Store the startingPhotoGroupId in state
            setStartingPhotoGroupId(photoGroupId);

            // Query the progressPhotos collection to find photos with the matching groupId
            const reportRef = firebase
              .firestore()
              .collection("organizations")
              .doc(organizationId)
              .collection("progressPhotos")
              .where("groupId", "==", photoGroupId);

            consola.debug(
              "++++++ READ -> useClientPhotos: getStartingProgressPhotos",
            );

            const data = await reportRef.get();

            const docs = data.docs;
            const groups = getPhotoGroups(docs, 1);

            // Use a temporary variable to avoid circular references
            setStartingPhotos(groups);
            isFetchingRef.current = false;
            return groups;
          }
        }
        setStartingPhotos({});
        isFetchingRef.current = false;
        return {};
      } catch (error) {
        console.error("Error fetching starting photos:", error);
        setStartingPhotos({});
        isFetchingRef.current = false;
        return {};
      } finally {
        setLoading(false);
        // Ensure we clear the fetching flag even in case of errors
        isFetchingRef.current = false;
      }
    },
    [startingPhotoGroupId, startingPhotos],
  ); // Dependencies to track

  /**
   * Get progress photos for a client based on date range
   */
  const getProgressPhotos = React.useCallback(
    async (uid, dateRange, limit = 50, daysBack = 31) => {
      if (!uid) return {};

      setLoading(true);
      try {
        const organizationId = getRecoil(organizationIdState);

        let start = moment().subtract(daysBack, "days").startOf("day").toDate();
        let end = moment().endOf("day").toDate();
        if (dateRange) {
          start = dateRange.start;
          end = dateRange.end;
        }

        const reportRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("progressPhotos")
          .where("clientId", "==", uid)
          .where("createdOn", ">=", start)
          .where("createdOn", "<=", end)
          .orderBy("createdOn", "desc");

        const data = await reportRef.get();
        consola.debug("++++++ READ -> useClientPhotos: getProgressPhotos");

        const groups = getPhotoGroups(data.docs, limit);
        setProgressPhotos(groups);
        return groups;
      } catch (error) {
        console.error("Error fetching progress photos:", error);
        setProgressPhotos({});
        return {};
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  /**
   * Delete progress photos by group ID using the new API endpoint
   */
  const deleteProgressPhotos = async (uid, groupId) => {
    console.log("🗑️ useClientPhotos: deleteProgressPhotos called", {
      uid,
      groupId,
    });
    setLoading(true);
    try {
      const organizationId = getRecoil(organizationIdState);

      // Call the new API endpoint
      const response = await fetch(
        "https://app.mighty45.com/api/v1/photos/delete-group",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            organizationId,
            clientId: uid,
            groupId,
          }),
        },
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.error || `HTTP ${response.status}: ${response.statusText}`,
        );
      }

      if (result.success) {
        console.log("🗑️ useClientPhotos: Successfully deleted photos via API", {
          deletedCount: result.deletedCount,
          details: result.details,
        });

        // Refresh is owned by the Photos tab (month-scoped); clear local group if present
        if (startingPhotos && Object.keys(startingPhotos).includes(groupId)) {
          setStartingPhotos({});
        }

        setProgressPhotos((prev) => {
          if (!prev || !(groupId in prev)) return prev;
          const next = { ...prev };
          delete next[groupId];
          return next;
        });

        return {
          success: true,
          message: result.message,
          deletedCount: result.deletedCount,
        };
      } else {
        throw new Error(result.error || "API returned success: false");
      }
    } catch (error) {
      console.error("🗑️ Error deleting progress photos:", error);
      return {
        success: false,
        message: error.message || "Failed to delete progress photos",
        error: error,
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Set a photo group as the starting/reference photos
   */
  const setStartingPhotoGroup = async (uid, groupId) => {
    setLoading(true);
    try {
      const organizationId = getRecoil(organizationIdState);

      const clientRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(uid);

      consola.debug("++++++ READ -> useClientPhotos: setStartingPhotoGroup");
      const client = await clientRef.get();
      if (client.exists) {
        consola.debug("+++++ WRITE => useClientPhotos: setStartingPhotoGroup");
        await clientRef.set(
          {
            startingPhotoGroupId: groupId,
          },
          { merge: true },
        );

        // Update the starting photos after setting
        await getStartingProgressPhotos(uid);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error setting starting photo group:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch progress photo configuration from the organization
   */
  const fetchProgressPhotoConfig = async () => {
    try {
      const organizationId = getRecoil(organizationIdState);
      const orgRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId);

      consola.debug("++++++ READ -> useClientPhotos: fetchProgressPhotoConfig");
      const orgDoc = await orgRef.get();

      if (orgDoc.exists) {
        const data = orgDoc.data();
        if (data && data.progressPhotoConfiguration) {
          const sorted = _.sortBy(data.progressPhotoConfiguration, "index");
          setProgressPhotoConfig(sorted);
          return sorted;
        }
      }

      setProgressPhotoConfig([]);
      return [];
    } catch (error) {
      console.error("Error fetching progress photo configuration:", error);
      setProgressPhotoConfig([]);
      return [];
    }
  };

  /**
   * Get display name for a photo type based on its ID
   */
  const getPhotoDisplayName = React.useCallback(
    (typeId) => {
      if (!progressPhotoConfig || progressPhotoConfig.length === 0) {
        return typeId; // Fallback to the ID if no config is available
      }

      const photoType = progressPhotoConfig.find(
        (config) => config.id === typeId,
      );
      return photoType ? photoType.name : typeId;
    },
    [progressPhotoConfig],
  );

  // Format photos for the Enhanced Photos Tab
  const formatPhotosForDisplay = React.useCallback(
    (photoGroups) => {
      if (!photoGroups) return [];

      const formattedPhotos = [];

      Object.keys(photoGroups).forEach((groupId) => {
        const group = photoGroups[groupId];
        const createdOn = group.metadata.createdOn;
        // Store configuration to use for ordering later
        const configuration = group.metadata.configuration;

        Object.entries(group.photos).forEach(([type, url]) => {
          formattedPhotos.push({
            id: `${groupId}-${type}`,
            groupId: groupId,
            type: type,
            displayName: getPhotoDisplayName(type),
            url: url,
            date: createdOn,
            configuration: configuration, // Include the configuration for ordering
            // Could add weight data here if available in the metadata
          });
        });
      });

      return formattedPhotos.sort((a, b) => b.date - a.date);
    },
    [getPhotoDisplayName],
  ); // Adding getPhotoDisplayName as a dependency

  // On client change: load config + reference photos always.
  // Progress history is month-scoped by callers (Photos tab / daily reports).
  React.useEffect(() => {
    if (!clientId) {
      lastFetchedClientIdRef.current = null;
      return;
    }
    if (clientId === lastFetchedClientIdRef.current) return;
    lastFetchedClientIdRef.current = clientId;
    setProgressPhotos({});
    fetchProgressPhotoConfig();
    getStartingProgressPhotos(clientId);
  }, [clientId, getStartingProgressPhotos]);
  return React.useMemo(
    () => ({
      startingPhotos,
      progressPhotos,
      loading,
      startingPhotoGroupId,
      getStartingProgressPhotos,
      getProgressPhotos,
      deleteProgressPhotos,
      setStartingPhotoGroup,
      formatPhotosForDisplay,
      getPhotoGroups,
      progressPhotoConfig,
    }),
    [
      startingPhotos,
      progressPhotos,
      loading,
      startingPhotoGroupId,
      getStartingProgressPhotos,
      getProgressPhotos,
      deleteProgressPhotos,
      setStartingPhotoGroup,
      formatPhotosForDisplay,
      getPhotoGroups,
      progressPhotoConfig,
    ],
  );
};

export default useClientPhotos;
